import { useState, useEffect, useRef } from "react";

/**
 * Counts from 0 up to `target` over `duration` ms using
 * requestAnimationFrame, formatting the in-progress value with `formatFn`
 * on every tick. Pass a changing `playKey` to replay the animation (e.g.
 * an incrementing number bumped each time a tab is opened).
 */
export default function useCountUp(target, duration, formatFn, playKey) {
  const [display, setDisplay] = useState(formatFn(0));
  const frameRef = useRef(null);

  useEffect(() => {
    let start = null;

    function tick(ts) {
      if (start === null) start = ts;
      const elapsed = Math.min((ts - start) / duration, 1);
      setDisplay(formatFn(elapsed * target));
      if (elapsed < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(formatFn(target));
      }
    }

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration, playKey]);

  return display;
}

export const COUNT_FORMATTERS = {
  int: (v) => String(Math.round(v)),
  comma: (v) => Math.round(v).toLocaleString("en-US"),
  decimal: (v) => v.toFixed(1),
  percent: (v) => `${Math.round(v)}%`,
};
